import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircle, Eye, EyeOff, Lock } from "lucide-react";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { useToast } from "~/components/ui/use-toast";
import { supabase } from "~/integrations/supabase/client";

const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(6, "La contraseña debe tener al menos 6 caracteres"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Las contraseñas no coinciden",
    path: ["confirmPassword"],
  });

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  useEffect(() => {
    // Handle different types of recovery link formats
    const handleRecoveryLink = async () => {
      console.log("🔍 Full URL:", window.location.href);
      console.log("🔍 Pathname:", window.location.pathname);
      console.log("🔍 Search:", window.location.search);
      console.log("🔍 Hash:", window.location.hash);

      // Check current auth session
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();
      console.log("🔍 Current session:", session);
      console.log("🔍 Session error:", sessionError);

      // If user is already authenticated, they came from a valid recovery link
      if (session && session.user) {
        console.log("✅ User is already authenticated from recovery link!");
        console.log("✅ Ready to reset password!");
        return; // Exit early - user can now reset password
      }

      // Debug: Log all URL parameters
      console.log(
        "🔍 Query parameters:",
        Object.fromEntries(searchParams.entries())
      );

      // Method 1: Check for standard Supabase recovery parameters
      const token = searchParams.get("token");
      const type = searchParams.get("type");

      // Method 2: Check for direct access/refresh token parameters
      const accessToken = searchParams.get("access_token");
      const refreshToken = searchParams.get("refresh_token");

      // Method 3: Check for URL fragment parameters (after #)
      const urlHash = window.location.hash;
      console.log("🔍 URL hash:", urlHash);

      // Parse fragment parameters
      let fragmentParams: URLSearchParams | null = null;
      if (urlHash && urlHash.length > 1) {
        // Remove the # and parse as query string
        const hashQuery = urlHash.substring(1);
        fragmentParams = new URLSearchParams(hashQuery);
        console.log(
          "🔍 Fragment parameters:",
          Object.fromEntries(fragmentParams.entries())
        );
      }

      // Also check if Supabase has processed any auth change
      console.log("🔍 Checking auth state change...");

      // Set up auth state change listener for debugging
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((event, session) => {
        console.log("🔍 Auth state change event:", event);
        console.log("🔍 Auth state change session:", session);

        if (
          event === "TOKEN_REFRESHED" ||
          event === "SIGNED_IN" ||
          event === "INITIAL_SESSION"
        ) {
          if (session && session.user) {
            console.log(
              "✅ Auth success - user is authenticated and can reset password!"
            );
            // User is ready to reset password - no need to show error
          }
        }
      });

      // Clean up subscription after a few seconds
      setTimeout(() => {
        subscription.unsubscribe();
      }, 5000);

      // Check for tokens in fragments as well
      const fragmentToken =
        fragmentParams?.get("token") || fragmentParams?.get("token_hash");
      const fragmentType = fragmentParams?.get("type");
      const fragmentAccessToken = fragmentParams?.get("access_token");
      const fragmentRefreshToken = fragmentParams?.get("refresh_token");

      if (
        (token && type === "recovery") ||
        (fragmentToken && fragmentType === "recovery")
      ) {
        // Handle standard Supabase recovery link
        try {
          const tokenToUse = token || fragmentToken;
          console.log("🔑 Attempting to verify token:", tokenToUse);

          const { data, error } = await supabase.auth.verifyOtp({
            token_hash: tokenToUse!,
            type: "recovery",
          });

          console.log("🔑 Verification result data:", data);
          console.log("🔑 Verification result error:", error);

          if (error) {
            console.error("🔑 Verification failed:", error.message);
            throw error;
          }

          console.log("✅ Token verification successful!");
          // Token is valid, user is now authenticated
        } catch (error) {
          console.error("❌ Recovery verification error:", error);
          toast({
            title: "Enlace inválido",
            description:
              "El enlace de recuperación no es válido o ha expirado. Solicita uno nuevo.",
            variant: "destructive",
          });
          navigate("/login");
          return;
        }
      } else if (
        (accessToken && refreshToken) ||
        (fragmentAccessToken && fragmentRefreshToken)
      ) {
        // Handle direct token parameters
        try {
          const accessTokenToUse = accessToken || fragmentAccessToken;
          const refreshTokenToUse = refreshToken || fragmentRefreshToken;

          await supabase.auth.setSession({
            access_token: accessTokenToUse!,
            refresh_token: refreshTokenToUse!,
          });
        } catch (error) {
          console.error("Session setting error:", error);
          toast({
            title: "Enlace inválido",
            description:
              "El enlace de recuperación no es válido o ha expirado. Solicita uno nuevo.",
            variant: "destructive",
          });
          navigate("/login");
          return;
        }
      } else {
        // No valid parameters found - show debug info temporarily
        console.error("No valid recovery parameters found");
        console.log("Token:", token);
        console.log("Type:", type);
        console.log("Access Token:", accessToken);
        console.log("Refresh Token:", refreshToken);
        console.log("Fragment Token:", fragmentToken);
        console.log("Fragment Type:", fragmentType);
        console.log("Fragment Access Token:", fragmentAccessToken);
        console.log("Fragment Refresh Token:", fragmentRefreshToken);
        console.log("Full URL:", window.location.href);

        toast({
          title: "Enlace inválido",
          description:
            "El enlace de recuperación no es válido o ha expirado. Solicita uno nuevo.",
          variant: "destructive",
        });

        // Don't redirect immediately - let user see the debug info
        setTimeout(() => {
          navigate("/login");
        }, 5000);
        return;
      }
    };

    handleRecoveryLink();
  }, [searchParams, navigate, toast]);

  const onSubmit = async (values: ResetPasswordFormValues) => {
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: values.password,
      });

      if (error) {
        console.error("Password update error:", error);
        toast({
          title: "Error",
          description:
            "No se pudo actualizar la contraseña. Inténtalo de nuevo.",
          variant: "destructive",
        });
        return;
      }

      setIsSuccess(true);
      toast({
        title: "Contraseña actualizada",
        description: "Tu contraseña ha sido actualizada exitosamente.",
      });

      // Sign out the user so they need to login with new password
      await supabase.auth.signOut();

      // Redirect to login after a delay
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (error) {
      console.error("Unexpected error:", error);
      toast({
        title: "Error",
        description: "Hubo un problema inesperado. Inténtalo de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md space-y-8">
          <div className="flex flex-col items-center">
            <img
              src="/aliace-logo.jpg"
              alt="Logo"
              className="h-20 w-20 object-contain"
            />
            <h1 className="mt-4 text-2xl font-bold">ALIACE</h1>
          </div>

          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center space-y-4">
                <CheckCircle className="h-16 w-16 text-green-500" />
                <h2 className="text-center text-xl font-semibold">
                  ¡Contraseña Actualizada!
                </h2>
                <p className="text-center text-gray-600">
                  Tu contraseña ha sido actualizada exitosamente. Por seguridad,
                  debes iniciar sesión nuevamente con tu nueva contraseña.
                </p>
                <Button onClick={() => navigate("/login")} className="w-full">
                  Ir al Inicio de Sesión
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="flex flex-col items-center">
          <img
            src="/aliace-logo.jpg"
            alt="Logo"
            className="h-20 w-20 object-contain"
          />
          <h1 className="mt-4 text-2xl font-bold">ALIACE</h1>
          <p className="text-center text-gray-500">Restablecer Contraseña</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-center">
              <Lock className="h-5 w-5" />
              Nueva Contraseña
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
              >
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nueva Contraseña</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showPassword ? "text" : "password"}
                            placeholder="Mínimo 6 caracteres"
                            {...field}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirmar Contraseña</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Repetir contraseña"
                            {...field}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                          >
                            {showConfirmPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? "Actualizando..." : "Actualizar Contraseña"}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
